"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";
import { useInstagramActions } from "@/lib/hooks/useInstagram";
import { toast } from "@/components/ui/use-toast";
import { Loader2, ShieldAlert, AlertCircle, Instagram } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

function isPermissionError(err: unknown): boolean {
  const code = (err as any)?.code;
  if (code === "permission_denied") return true;
  const msg = ((err as any)?.message || String(err)).toLowerCase();
  return (
    msg.includes("access_denied") ||
    msg.includes("permission") ||
    msg.includes("scope") ||
    msg.includes("denied") ||
    msg.includes("not authorized") ||
    msg.includes("authorization") ||
    msg.includes("insufficient")
  );
}

export default function InstagramCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useLanguageNavigate();
  const instagram = useInstagramActions();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isPermission, setIsPermission] = useState(false);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      const checkStr = errorParam + " " + (searchParams.get("error_description") || "");
      const rawErr = { message: checkStr };
      const permErr = isPermissionError(rawErr);
      setIsPermission(permErr);
      if (permErr) {
        setError(t("instagram.insufficientPermissions"));
      } else {
        setError(searchParams.get("error_description") || t("instagram.oauthFailed"));
        setTimeout(() => navigate("/account"), 4000);
      }
      return;
    }

    if (!code || !state) {
      setError(t("instagram.invalidCallback"));
      setTimeout(() => navigate("/account"), 3000);
      return;
    }

    instagram
      .handleCallback(code, state)
      .then((result) => {
        toast({
          title: t("instagram.connected"),
          description: t("instagram.connectedDesc", { username: result.instagramUsername }),
        });
        navigate("/account");
      })
      .catch((err) => {
        const permErr = isPermissionError(err);
        setIsPermission(permErr);
        if (permErr) {
          setError(t("instagram.insufficientPermissions"));
        } else {
          setError((err as Error).message || t("instagram.oauthFailed"));
          setTimeout(() => navigate("/account"), 4000);
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = async () => {
    setError(null);
    setIsPermission(false);
    try {
      const { authUrl } = await instagram.getAuthUrl();
      window.location.href = authUrl;
    } catch {
      navigate("/account");
    }
  };

  return (
    <div className="min-h-screen bg-lavender flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-8 max-w-md w-full flex flex-col items-center gap-6 text-center">
        {error ? (
          isPermission ? (
            <>
              <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200">
                <ShieldAlert className="w-10 h-10 text-amber-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-bold">{t("instagram.permissionDeniedTitle")}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{error}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button
                  onClick={handleRetry}
                  className="flex-1 bg-linear-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg h-11"
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  {t("instagram.tryAgain")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/account")}
                  className="flex-1 rounded-lg h-11"
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 rounded-2xl bg-red-50 border border-red-200">
                <AlertCircle className="w-10 h-10 text-destructive" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-bold text-destructive">{t("instagram.oauthFailed")}</h2>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <p className="text-xs text-muted-foreground">{t("instagram.redirecting")}</p>
            </>
          )
        ) : (
          <>
            <div className="p-4 rounded-2xl bg-linear-to-br from-purple-50 to-pink-50 border border-purple-100">
              <Loader2 className="w-10 h-10 animate-spin text-purple-500" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-bold">{t("instagram.connectAccount")}</h2>
              <p className="text-sm text-muted-foreground">{t("instagram.connecting")}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
